import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image as ImageIcon, Mail, Upload, X, Plus } from 'lucide-react';
import { useAuth, ApiClientError } from '@techsofit/app-kit';
import type { CustomReminder, CustomReminderChannel, ReminderDirection } from '@techsofit/api-types';
import { Button, Card, Input, Textarea, Select, Toggle } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useGymData } from '../data/GymDataProvider';
import {
  updateGymSettings,
  uploadGymImage,
  getCustomReminders,
  createCustomReminder,
  updateCustomReminder,
  deleteCustomReminder,
} from '../lib/gymApi';
import { useAsync } from '../lib/useAsync';
import { isIntlPhone, normalizeIntlPhone } from '../lib/phone';

export function SettingsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'GYM_OWNER';

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow="Settings"
        title="Business settings"
        description={isOwner ? 'Manage your business’s details.' : 'Your business’s details.'}
      />

      {/* Two balanced columns: the tall card alone on the right, the two shorter
          cards stacked on the left, so the columns end at a similar height. */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <BrandingCard isOwner={isOwner} />
          <MembershipReminderCard isOwner={isOwner} />
          <CustomRemindersCard isOwner={isOwner} />
        </div>
        <div className="flex flex-col gap-4">
          <GymDetailsCard isOwner={isOwner} />
          <GuestPassCard isOwner={isOwner} />
        </div>
      </div>
    </section>
  );
}

function BrandingCard({ isOwner }: { isOwner: boolean }) {
  const { settings, reloadSettings } = useGymData();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Choose an image file.');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      setError('Image is too large (max 6MB).');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const { url } = await uploadGymImage(file, file.name);
      await updateGymSettings({ logoUrl: url });
      reloadSettings();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Upload failed. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function removeLogo() {
    setError(null);
    setBusy(true);
    try {
      await updateGymSettings({ logoUrl: '' });
      reloadSettings();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not remove the logo.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-ink">Branding</h2>
        <p className="mt-1 text-sm text-muted">
          Your logo shows on your sign-in page at{' '}
          <span className="font-medium text-ink">{window.location.host}</span>.
        </p>
      </div>
      <div className="flex items-center gap-4">
        <div className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-card border border-line bg-surface">
          {settings.logoUrl ? (
            <img src={settings.logoUrl} alt="Business logo" className="size-full object-contain" />
          ) : (
            <ImageIcon className="size-7 text-muted" aria-hidden />
          )}
        </div>
        {isOwner && (
          <div className="flex flex-col items-start gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} />
            <Button size="sm" onClick={() => fileRef.current?.click()} loading={busy}>
              <Upload className="size-4" aria-hidden />
              {settings.logoUrl ? 'Replace logo' : 'Upload logo'}
            </Button>
            {settings.logoUrl && (
              <button
                type="button"
                onClick={removeLogo}
                disabled={busy}
                className="text-sm text-muted hover:text-bad-ink"
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
      {error && <p className="text-sm font-medium text-bad-ink">{error}</p>}
      <p className="text-xs text-muted">A PNG or SVG with a transparent background works best. Max 6MB.</p>
    </Card>
  );
}

function MembershipReminderCard({ isOwner }: { isOwner: boolean }) {
  const { settings, reloadSettings, subscription } = useGymData();
  const init = parseOffsets(settings.expiryReminderOffsets ?? '7,1,0');
  const [before, setBefore] = useState<string[]>(init.before.map(String));
  const [onExpiry, setOnExpiry] = useState(init.onExpiry);
  const [winBack, setWinBack] = useState(String(settings.winBackDays ?? 30));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gym = settings.name || 'your business';

  function setRow(i: number, v: string) {
    setBefore((b) => b.map((x, idx) => (idx === i ? v.replace(/[^0-9]/g, '') : x)));
  }

  async function save() {
    setError(null);
    setSaving(true);
    try {
      const nums = [...new Set(before.map((s) => parseInt(s, 10)).filter((n) => !isNaN(n) && n > 0))]
        .sort((a, b) => b - a);
      const offsets = [...nums, ...(onExpiry ? [0] : [])].join(',') || '0';
      await updateGymSettings({
        expiryReminderOffsets: offsets,
        winBackDays: Math.max(0, parseInt(winBack, 10) || 0),
      });
      reloadSettings();
      flash(setSaved);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="flex flex-col gap-5 p-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-ink">Membership reminders</h2>
        <p className="mt-1 text-sm text-muted">
          Reminders go out <strong>automatically</strong>. Just set when they fire; the message is written for you.
        </p>
      </div>

      {!subscription.canSendSms && (
        <div className="rounded-control bg-accent-soft p-3 text-sm text-ink">
          Your plan sends reminders by <strong>email</strong>. <Link to="/billing" className="font-medium underline">Upgrade to Pro</Link> to also reach members by SMS.
        </div>
      )}

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-ink-soft">Before a membership expires</p>
        {before.length === 0 && <p className="text-sm text-muted">No pre-expiry reminders yet.</p>}
        {before.map((v, i) => (
          <div key={i} className="rounded-control border border-line bg-elevated p-3">
            <div className="flex items-center gap-2">
              <div className="w-20">
                <Input label="Days before expiry" hideLabel inputMode="numeric" value={v} onChange={(e) => setRow(i, e.target.value)} disabled={!isOwner} />
              </div>
              <span className="text-sm text-ink-soft">day{v === '1' ? '' : 's'} before it expires</span>
              {isOwner && (
                <button type="button" onClick={() => setBefore((b) => b.filter((_, idx) => idx !== i))} className="ml-auto text-muted hover:text-bad-ink" aria-label="Remove this reminder">
                  <X className="size-4" aria-hidden />
                </button>
              )}
            </div>
            <p className="mt-2 text-xs italic text-muted">
              “Hi Alex, your {gym} membership expires on Sat 12 Jul. Pop in or message the front desk to renew.”
            </p>
          </div>
        ))}
        {isOwner && (
          <button type="button" onClick={() => setBefore((b) => [...b, ''])} className="self-start text-sm font-medium text-ink underline">
            + Add a reminder
          </button>
        )}
      </div>

      <Toggle
        checked={onExpiry}
        onChange={setOnExpiry}
        disabled={!isOwner}
        label="Also message on the expiry day"
        description={onExpiry
          ? `“Hi Alex, your ${gym} membership has expired. Renew at the front desk to keep training.”`
          : 'Off — nothing is sent on the day it expires.'}
      />

      <div>
        <p className="mb-1.5 text-sm font-medium text-ink-soft">Win-back — days after a membership lapses</p>
        <div className="w-28">
          <Input label="Win-back days" hideLabel inputMode="numeric" value={winBack} onChange={(e) => setWinBack(e.target.value.replace(/[^0-9]/g, ''))} disabled={!isOwner} />
        </div>
        <p className="mt-2 text-xs italic text-muted">
          {(parseInt(winBack, 10) || 0) > 0
            ? `“Hi Alex, we miss you at ${gym}! Your membership lapsed on Sat 12 Jul — come back this week.” (email)`
            : 'Off — no win-back message is sent.'}
        </p>
      </div>

      {isOwner && <SaveRow onSave={save} saving={saving} saved={saved} error={error} />}
    </Card>
  );
}

/** Split the stored "days before expiry" CSV into pre-expiry offsets + the expiry-day (0) flag. */
function parseOffsets(csv: string): { before: number[]; onExpiry: boolean } {
  const nums = csv.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
  return { before: nums.filter((n) => n > 0).sort((a, b) => b - a), onExpiry: nums.includes(0) };
}

/** Owner-authored custom reminders (up to 3). Each fires N days before/after expiry. */
function CustomRemindersCard({ isOwner }: { isOwner: boolean }) {
  const { subscription } = useGymData();
  const { data, loading, reload } = useAsync(getCustomReminders);
  const reminders = data ?? [];
  const [adding, setAdding] = useState(false);

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-ink">Your own reminders</h2>
        <p className="mt-1 text-sm text-muted">
          Add up to 3 custom messages that fire a set number of days before or after expiry. Use{' '}
          <code className="rounded bg-surface px-1">{'{name}'}</code>,{' '}
          <code className="rounded bg-surface px-1">{'{gym}'}</code>,{' '}
          <code className="rounded bg-surface px-1">{'{date}'}</code> and we'll fill them in.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : (
        <>
          {reminders.map((r) => (
            <ReminderEditor key={r.id} reminder={r} canSms={subscription.canSendSms} disabled={!isOwner} onDone={reload} />
          ))}
          {reminders.length === 0 && !adding && <p className="text-sm text-muted">No custom reminders yet.</p>}
          {adding && (
            <ReminderEditor canSms={subscription.canSendSms} disabled={!isOwner} onDone={() => { setAdding(false); reload(); }} />
          )}
          {isOwner && !adding && reminders.length < 3 && (
            <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1 self-start text-sm font-medium text-ink underline">
              <Plus className="size-4" aria-hidden /> Add a reminder
            </button>
          )}
          {reminders.length >= 3 && <p className="text-xs text-muted">You've reached the maximum of 3 custom reminders.</p>}
        </>
      )}
    </Card>
  );
}

function ReminderEditor({ reminder, canSms, disabled, onDone }: {
  reminder?: CustomReminder;
  canSms: boolean;
  disabled: boolean;
  onDone: () => void;
}) {
  const [direction, setDirection] = useState<ReminderDirection>(reminder?.direction ?? 'BEFORE');
  const [days, setDays] = useState(String(reminder?.days ?? 3));
  const [message, setMessage] = useState(reminder?.message ?? 'Hi {name}, a quick note from {gym} about your membership.');
  const [channel, setChannel] = useState<CustomReminderChannel>(reminder?.channel ?? 'EMAIL');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (!message.trim()) { setError('Write a message.'); return; }
    setSaving(true);
    try {
      const body = { direction, days: Math.max(0, parseInt(days, 10) || 0), message: message.trim(), channel, enabled: reminder?.enabled ?? true };
      if (reminder) await updateCustomReminder(reminder.id, body);
      else await createCustomReminder(body);
      onDone();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!reminder) { onDone(); return; }
    setSaving(true);
    try {
      await deleteCustomReminder(reminder.id);
      onDone();
    } finally {
      setSaving(false);
    }
  }

  const smsChosen = channel === 'SMS' || channel === 'BOTH';
  return (
    <div className="flex flex-col gap-3 rounded-control border border-line bg-elevated p-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-16 shrink-0">
          <Input label="Days" hideLabel inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/[^0-9]/g, ''))} disabled={disabled} />
        </div>
        <div className="min-w-[8rem] flex-1">
          <Select label="Timing" hideLabel value={direction} onChange={(e) => setDirection(e.target.value as ReminderDirection)} disabled={disabled}>
            <option value="BEFORE">days before expiry</option>
            <option value="AFTER">days after expiry</option>
          </Select>
        </div>
        <div className="w-32 shrink-0">
          <Select label="Channel" hideLabel value={channel} onChange={(e) => setChannel(e.target.value as CustomReminderChannel)} disabled={disabled}>
            <option value="EMAIL">Email</option>
            <option value="SMS">SMS</option>
            <option value="BOTH">Email + SMS</option>
          </Select>
        </div>
      </div>
      <Textarea label="Message" value={message} onChange={(e) => setMessage(e.target.value)} rows={2} maxLength={500} disabled={disabled} placeholder="Hi {name}, …" />
      {smsChosen && !canSms && (
        <p className="text-xs text-warn-ink">SMS needs Pro — this will send by email until you upgrade.</p>
      )}
      {error && <p className="text-xs text-bad-ink">{error}</p>}
      {!disabled && (
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={save} loading={saving}>Save</Button>
          <button type="button" onClick={remove} disabled={saving} className="text-sm text-muted hover:text-bad-ink">
            {reminder ? 'Delete' : 'Cancel'}
          </button>
        </div>
      )}
    </div>
  );
}

function GymDetailsCard({ isOwner }: { isOwner: boolean }) {
  const { settings, reloadSettings } = useGymData();
  const [name, setName] = useState(settings.name);
  const [contactPhone, setContactPhone] = useState(settings.contactPhone ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (contactPhone.trim() && !isIntlPhone(contactPhone)) {
      setError('Enter the contact phone in international format, e.g. +263 77 123 4567 (or leave it blank).');
      return;
    }
    setSaving(true);
    try {
      await updateGymSettings({
        name: name.trim(),
        contactPhone: contactPhone.trim() ? normalizeIntlPhone(contactPhone) : '',
      });
      reloadSettings();
      flash(setSaved);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="flex flex-col gap-4 p-5">
      <h2 className="font-display text-lg font-semibold text-ink">Business details</h2>
      <Input label="Business name" value={name} onChange={(e) => setName(e.target.value)} disabled={!isOwner} />
      <Input label="Contact phone" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} disabled={!isOwner} placeholder="+263 77 123 4567" />
      {isOwner && <SaveRow onSave={save} saving={saving} saved={saved} error={error} />}
    </Card>
  );
}

function GuestPassCard({ isOwner }: { isOwner: boolean }) {
  const { settings, reloadSettings } = useGymData();
  const [enabled, setEnabled] = useState(settings.guestPassEnabled);
  // Default so the fields are never blank/"undefined" (e.g. before settings refetch).
  const [perMonth, setPerMonth] = useState(String(settings.guestPassPerMemberPerMonth ?? 1));
  const [validity, setValidity] = useState(String(settings.guestPassValidityDays ?? 14));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    setSaving(true);
    try {
      await updateGymSettings({
        guestPassEnabled: enabled,
        guestPassPerMemberPerMonth: Math.max(1, parseInt(perMonth, 10) || 1),
        guestPassValidityDays: Math.max(1, parseInt(validity, 10) || 1),
      });
      reloadSettings();
      flash(setSaved);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div>
        <h2 className="font-display text-lg font-semibold text-ink">Guest passes</h2>
        <p className="mt-1 text-sm text-muted">
          Let members invite a friend for a free visit. Guests get a code to bring to the front desk, and you can
          track who invited whom and who joined.
        </p>
      </div>

      <Toggle
        checked={enabled}
        disabled={!isOwner}
        onChange={setEnabled}
        label="Enable guest passes"
        description={enabled ? 'Members can invite a friend from their app.' : 'Off — members won’t see the invite option.'}
      />

      {enabled && (
        <div className="grid grid-cols-2 gap-3 rounded-control border border-line bg-surface p-3">
          <Input
            label="Passes / member / month"
            inputMode="numeric"
            value={perMonth}
            onChange={(e) => setPerMonth(e.target.value.replace(/[^0-9]/g, ''))}
            disabled={!isOwner}
          />
          <Input
            label="Valid for (days)"
            inputMode="numeric"
            value={validity}
            onChange={(e) => setValidity(e.target.value.replace(/[^0-9]/g, ''))}
            disabled={!isOwner}
          />
        </div>
      )}
      {isOwner && <SaveRow onSave={save} saving={saving} saved={saved} error={error} />}
    </Card>
  );
}

function SaveRow({ onSave, saving, saved, error }: { onSave: () => void; saving: boolean; saved: boolean; error: string | null }) {
  return (
    <div className="flex items-center justify-end gap-3">
      {error && <span className="mr-auto text-sm font-medium text-bad-ink">{error}</span>}
      {saved && <span className="mr-auto inline-flex items-center gap-1 text-sm font-medium text-ok-ink"><Mail className="size-4" aria-hidden /> Saved</span>}
      <Button size="sm" onClick={onSave} loading={saving}>
        Save
      </Button>
    </div>
  );
}

/** Briefly show a "saved" confirmation. */
function flash(set: (v: boolean) => void) {
  set(true);
  setTimeout(() => set(false), 2000);
}
